import { BasicBarChartConfig, BasicCartesianChartConfig } from '@app/data/interface/widget-config';
import {
  BasicBarChartColourPalette,
  BasicBarChartEchartConfigTemplate,
} from '@app/data/widget-config-templates/basic-bar-chart';
import {
  BasicCartesianChartEchartConfigTemplate,
  BasicCartesianChartAxisTemplate,
  BasicCartesianChartColourPalette,
} from '@app/data/widget-config-templates/basic-cartesian-chart';
import { dataViewFormatter } from '@app/widgets/helpers/widgets';
import { EChartsOption } from 'echarts';
export const IslandBlockRoadTotalNutrients: BasicCartesianChartConfig = {
  echartsConfig: {
    ...BasicCartesianChartEchartConfigTemplate,
    yAxis: [
      {
        ...BasicCartesianChartAxisTemplate,
        name: 'TKN\ng/m³',
      },
      {
        ...BasicCartesianChartAxisTemplate,
        name: 'P\ng/m³',
        position: 'right',
        axisLine: {
          show: true,
          lineStyle: {
            color: BasicCartesianChartColourPalette[1],
          },
        },
        axisLabel: {
          color: BasicCartesianChartColourPalette[1],
        },
      },
    ],
    series: [
      {
        type: 'line',
        yAxisIndex: 0,
        name: 'Total Kjeldahl Nitrogen',
        symbolSize: 13,
      },
      {
        type: 'line',
        yAxisIndex: 1,
        name: 'Total Phosphorus',
        symbolSize: 13,
      },
    ],
  },
  dataConfig: {
    title: 'Island Block Nutrients',
    widgetId: 1,
    domain: 'wai',
    dataSources: [
      {
        environmentalDataSourceIdentifier: '1293/7',
        analyteIdentifier: 'total-kjeldahl-nitrogen',
        valueField: 'avg',
        label: 'Total Kjeldahl Nitrogen',
      },
      {
        environmentalDataSourceIdentifier: '1293/7',
        analyteIdentifier: 'total-phosphorus',
        valueField: 'avg',
        label: 'Total Phosphorus',
      },
    ],
  },
};
export const IslandBlockRoadWaterChemistry: BasicCartesianChartConfig = {
  echartsConfig: {
    ...BasicCartesianChartEchartConfigTemplate,
    yAxis: [
      {
        ...BasicCartesianChartAxisTemplate,
        name: '°C',
      },
      {
        ...BasicCartesianChartAxisTemplate,
        name: 'DO\ng/m³',
        position: 'right',
        axisLine: {
          show: true,
          lineStyle: {
            color: BasicCartesianChartColourPalette[1],
          },
        },
        axisLabel: {
          color: BasicCartesianChartColourPalette[1],
        },
      },
      {
        ...BasicCartesianChartAxisTemplate,
        name: 'NNN\ng/m³',
        position: 'right',
        offset: 40,
        axisLine: {
          show: true,
          lineStyle: {
            color: BasicCartesianChartColourPalette[2],
          },
        },
        axisLabel: {
          color: BasicCartesianChartColourPalette[2],
        },
      },
    ],
    series: [
      {
        type: 'line',
        yAxisIndex: 0,
        name: 'Water Temperature',
        symbolSize: 13,
        tooltip: {
          valueFormatter: (value) => value + '°C',
        },
      },
      {
        type: 'line',
        yAxisIndex: 1,
        name: 'Dissolved Oxygen',
        symbolSize: 13,
        tooltip: {
          valueFormatter: (value) => value + ' g/m³',
        },
      },
      {
        type: 'line',
        yAxisIndex: 2,
        name: 'Nitrate Nitrite Nitrogen',
        symbolSize: 13,
        tooltip: {
          valueFormatter: (value) => value + ' g/m³',
        },
      },
    ],
  },
  dataConfig: {
    title: 'Island Block Road Water Chemistry',
    widgetId: 2,
    domain: 'wai',
    dataSources: [
      {
        environmentalDataSourceIdentifier: '1293/7',
        analyteIdentifier: 'water-temperature',
        valueField: 'avg',
        label: 'Water Temperature',
      },
      {
        environmentalDataSourceIdentifier: '1293/7',
        analyteIdentifier: 'dissolved-oxygen-mg-per-cubic-metre',
        valueField: 'avg',
        label: 'Dissolved Oxygen',
      },
      {
        environmentalDataSourceIdentifier: '1293/7',
        analyteIdentifier: 'nitrate-+-nitrite-nitrogen-grams-per-cubic-metre',
        valueField: 'avg',
        label: 'Nitrate Nitrite Nitrogen',
      },
    ],
  },
};
export const ControlStructureRainfall: BasicBarChartConfig = {
  echartsConfig: {
    ...BasicBarChartEchartConfigTemplate,
    tooltip: {
      ...BasicBarChartEchartConfigTemplate.tooltip,
      position: function (pt: any) {
        return [pt[0], '10%'];
      },
    },
    xAxis: {
      ...BasicBarChartEchartConfigTemplate.xAxis,
      boundaryGap: true,
    },
    yAxis: [
      {
        ...BasicCartesianChartAxisTemplate,
        name: 'mm/hr',
        boundaryGap: undefined,
      },
      {
        ...BasicCartesianChartAxisTemplate,
        name: 'mm',
        position: 'right',
        boundaryGap: undefined,
        axisLine: {
          show: true,
          lineStyle: {
            color: BasicBarChartColourPalette[1],
          },
        },
        axisLabel: {
          color: BasicBarChartColourPalette[1],
        },
      },
    ],
    series: [
      {
        type: 'bar',
        yAxisIndex: 0,
        name: 'mm/hr (avg)',
      },
      {
        type: 'line',
        yAxisIndex: 1,
        name: 'mm (total)',
      },
    ],
  },
  dataConfig: {
    title: 'Control Structure Rainfall',
    widgetId: 3,
    domain: 'wai',
    showBoundary: true,
    boundary: 5,
    dataSources: [
      {
        environmentalDataSourceIdentifier: '1293/6',
        analyteIdentifier: 'precipitation',
        valueField: 'avg',
        label: 'Precipitation',
      },
      {
        environmentalDataSourceIdentifier: '1293/6',
        analyteIdentifier: 'precipitation',
        valueField: 'sum',
        label: 'Precipitation',
      },
    ],
  },
};
export const WaterLevels: BasicBarChartConfig = {
  echartsConfig: {
    ...BasicBarChartEchartConfigTemplate,
    tooltip: {
      ...BasicBarChartEchartConfigTemplate.tooltip,
      axisPointer: undefined,
      position: function (pt: any) {
        return [pt[0], '10%'];
      },
    },
    xAxis: {
      ...BasicBarChartEchartConfigTemplate.xAxis,
      boundaryGap: true,
    },
    yAxis: [
      {
        ...BasicCartesianChartAxisTemplate,
        name: 'Metres',
        boundaryGap: undefined,
        axisLine: {
          show: true,
        },
        axisLabel: {
          color: '#008ACD',
        },
      },
    ],
    series: [
      {
        type: 'bar',
        yAxisIndex: 0,
        name: 'Falls Road',
      },
      {
        type: 'bar',
        yAxisIndex: 0,
        name: 'Ropeway',
      },
      {
        type: 'bar',
        yAxisIndex: 0,
        name: 'Mercer',
      },
    ],
  },
  dataConfig: {
    title: 'Water Levels',
    widgetId: 3,
    domain: 'wai',
    showBoundary: true,
    boundary: 0.5,
    dataSources: [
      {
        environmentalDataSourceIdentifier: '1293/11',
        analyteIdentifier: 'relative-water-level',
        valueField: 'avg',
        label: 'Falls Road',
      },
      {
        environmentalDataSourceIdentifier: '1293/15',
        analyteIdentifier: 'relative-water-level',
        valueField: 'avg',
        label: 'Ropeway',
      },
      {
        environmentalDataSourceIdentifier: '1131/91',
        analyteIdentifier: 'relative-water-level',
        valueField: 'avg',
        label: 'Mercer',
      },
    ],
  },
};
export const IslandBlockRoadDOM: BasicBarChartConfig = {
  echartsConfig: {
    ...BasicBarChartEchartConfigTemplate,
    tooltip: {
      ...BasicBarChartEchartConfigTemplate.tooltip,
      position: function (pt: any) {
        return [pt[0], '10%'];
      },
    },
    xAxis: {
      ...BasicBarChartEchartConfigTemplate.xAxis,
      boundaryGap: true,
    },
    yAxis: [
      {
        ...BasicCartesianChartAxisTemplate,
        name: 'cm',
        boundaryGap: undefined,
        axisLine: {
          show: true,
        },
        axisLabel: {
          color: '#008ACD',
        },
      },
    ],
    series: [
      {
        type: 'bar',
        yAxisIndex: 0,
        name: 'Absorbance @ 340 nm filtered',
        tooltip: {
          valueFormatter: (value) => value + ' cm',
        },
      },
      {
        type: 'bar',
        yAxisIndex: 0,
        name: 'Absorbance @ 440 nm filtered',
        tooltip: {
          valueFormatter: (value) => value + 'cm',
        },
      },
    ],
  },
  dataConfig: {
    title: 'Island Block - Dissolved Organic Matter',
    widgetId: 5,
    domain: 'wai',
    boundary: 0.5,
    showBoundary: true,
    dataSources: [
      {
        environmentalDataSourceIdentifier: '1293/7',
        analyteIdentifier: 'absorbance-@-340-nm-filtered',
        valueField: 'avg',
        label: 'Absorbance @ 340 nm filtered',
      },
      {
        environmentalDataSourceIdentifier: '1293/7',
        analyteIdentifier: 'absorbance-@-440-nm-filtered',
        valueField: 'avg',
        label: 'Absorbance @ 440 nm filtered',
      },
    ],
  },
};
